/**
 * The shape of one project in the account's list (`workspaces[]` from
 * GET /planning/me), tolerant of the pre-port `wedding_*` spellings the API
 * still sends as aliases.
 *
 * Money rule: the contract value is exact text labelled with the project's own
 * currency. It is dropped here for anyone who may not see money, even if the
 * server sent it, so no screen can show it by mistake.
 */
import type { UserRole } from '../types/User';
import { isFinanciallyBlind } from './roles';

export type PipelineStage = 'lead' | 'proposal' | 'booked' | 'delivering' | 'won' | 'lost';

const STAGES: readonly PipelineStage[] = ['lead', 'proposal', 'booked', 'delivering', 'won', 'lost'];

export interface Workspace {
  project_id: string;
  project_name: string;
  /** The tenant above the projects (one per account). */
  workspace_id: string | null;
  role: UserRole;
  /** True for a contractor, an unknown role, or a person the owner flagged. */
  financially_blind: boolean;
  project_date: string | null;
  pipeline_stage: PipelineStage;
  /** Exact decimal text, or null when unset or hidden from this person. */
  contract_value: string | null;
  /** Currency the contract value is quoted in; null when unset or hidden. */
  contract_currency: string | null;
  readiness: number;
  /** Whether the project has any tasks, when the server says (has_tasks, or a task_total / task_count); null when it does not. */
  has_tasks?: boolean | null;
  archived_at?: string | null;
}

/** True/false when the response says whether the project has tasks; null when it says nothing. */
function hasTasksOf(raw: Record<string, unknown>): boolean | null {
  if (typeof raw.has_tasks === 'boolean') return raw.has_tasks;
  for (const v of [raw.task_total, raw.task_count]) {
    if ((typeof v === 'number' || (typeof v === 'string' && v.trim() !== '')) && Number.isFinite(Number(v))) return Number(v) > 0;
  }
  return null;
}

function text(v: unknown): string | null {
  if (typeof v === 'string' && v.trim()) return v;
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  return null;
}

export function normalizeWorkspace(raw: Record<string, unknown>): Workspace {
  const role = raw.role as UserRole;
  const blind = isFinanciallyBlind(role, raw.financially_blind === true);
  const stage = raw.pipeline_stage as PipelineStage;
  return {
    project_id: String(raw.project_id ?? raw.wedding_id ?? ''),
    project_name: String(raw.project_name ?? raw.wedding_name ?? ''),
    workspace_id: text(raw.workspace_id),
    role,
    financially_blind: blind,
    project_date: text(raw.project_date ?? raw.wedding_date),
    pipeline_stage: STAGES.includes(stage) ? stage : 'lead',
    contract_value: blind ? null : text(raw.contract_value),
    contract_currency: blind ? null : text(raw.contract_currency),
    readiness: Number.isFinite(Number(raw.readiness)) ? Number(raw.readiness) : 0,
    has_tasks: hasTasksOf(raw),
    archived_at: text(raw.archived_at),
  };
}
